import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { UserContext } from 'globalContext/UserContext';
import moment from 'moment';
import PageHeader from 'modules/Orders/PageHeader';
import { ContractType } from 'modules/Orders/types';
import { RoleTypes } from 'utils/rolesTypes';
import {
  fetchEmploymentAccounts,
  fetchEmploymentForm,
  updateEmploymentForm,
} from './api';
import {
  EmploymentAccountOption,
  EmploymentForm,
  EmploymentFormHistoryEntry,
} from './types';

const CONTRACT_TYPES: ContractType[] = [
  'B2B',
  'CIVIL_CONTRACT',
  'EMPLOYMENT_CONTRACT',
];

type AccessState = 'checking' | 'authorized' | 'unauthorized' | 'error';

const contractLabel = (contractType: ContractType | null) =>
  contractType ? `BUSINESS_TRIP.${contractType}` : 'EMPLOYMENT_FORM.NOT_SET';

export const historyStatus = (
  entry: EmploymentFormHistoryEntry,
  today: string,
) => {
  if (entry.appliedAt) {
    return 'APPLIED';
  }
  return entry.effectiveDate > today ? 'SCHEDULED' : 'PENDING';
};

const formatTimestamp = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY HH:mm') : '—';

// Searchable by first name, surname and username.
const accountLabel = ({ name, surname, username }: EmploymentAccountOption) =>
  `${name} ${surname} (${username})`;

const EmploymentForms = () => {
  const intl = useIntl();
  const { roles, username } = useContext(UserContext);
  const privilegedByRole = useMemo(
    () =>
      roles.includes(RoleTypes.RoleAccounting) ||
      roles.includes(RoleTypes.RoleDEVHR),
    [roles],
  );
  const [access, setAccess] = useState<AccessState>('checking');
  const [accounts, setAccounts] = useState<EmploymentAccountOption[]>([]);
  const [accountsError, setAccountsError] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState('');
  const [employmentForm, setEmploymentForm] = useState<EmploymentForm>();
  const [loadingForm, setLoadingForm] = useState(false);
  const [formError, setFormError] = useState(false);
  const [staleResponse, setStaleResponse] = useState(false);
  const [contractType, setContractType] = useState<ContractType>('B2B');
  const [effectiveDate, setEffectiveDate] = useState(
    moment().format('YYYY-MM-DD'),
  );
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updating, setUpdating] = useState(false);
  const loadSequence = useRef(0);
  const selectedUsernameRef = useRef('');
  const mounted = useRef(true);

  const applyEmploymentForm = useCallback((data: EmploymentForm) => {
    setEmploymentForm(data);
    setContractType(
      data.history[0]?.newContractType ?? data.contractType ?? 'B2B',
    );
    setEffectiveDate(moment().format('YYYY-MM-DD'));
    setReason('');
    setValidationError('');
    setUpdateError('');
    setUpdateSuccess(false);
  }, []);

  const loadForm = useCallback(
    async (targetUsername: string) => {
      const sequence = ++loadSequence.current;
      selectedUsernameRef.current = targetUsername;
      setSelectedUsername(targetUsername);
      setLoadingForm(true);
      setFormError(false);
      setStaleResponse(false);
      setUpdateSuccess(false);

      const result = await fetchEmploymentForm(targetUsername);
      if (!mounted.current) {
        return;
      }
      if (sequence !== loadSequence.current) {
        setStaleResponse(true);
        return;
      }

      setStaleResponse(false);
      setLoadingForm(false);
      if (result.status === 403) {
        setAccess('unauthorized');
        return;
      }
      if (!result.ok || !result.data) {
        setEmploymentForm(undefined);
        setFormError(true);
        return;
      }
      setAccess('authorized');
      applyEmploymentForm(result.data);
    },
    [applyEmploymentForm],
  );

  const loadAccounts = useCallback(
    async (preferredUsername: string) => {
      const result = await fetchEmploymentAccounts();
      if (!mounted.current) {
        return;
      }
      if (!result.ok || !result.data) {
        setAccountsError(true);
        return;
      }
      const sorted = [...result.data].sort((left, right) =>
        `${left.surname} ${left.name}`.localeCompare(
          `${right.surname} ${right.name}`,
        ),
      );
      setAccounts(sorted);
      setAccountsError(false);

      const preferred = sorted.find(
        (account) => account.username === preferredUsername,
      );
      const target = preferred?.username ?? sorted[0]?.username;
      if (target && target !== selectedUsernameRef.current) {
        await loadForm(target);
      }
    },
    [loadForm],
  );

  useEffect(() => {
    mounted.current = true;
    const initialize = async () => {
      if (!username) {
        return;
      }

      if (privilegedByRole) {
        setAccess('authorized');
        await loadAccounts(username);
        return;
      }

      const result = await fetchEmploymentForm(username);
      if (!mounted.current) {
        return;
      }
      if (result.status === 403) {
        setAccess('unauthorized');
        return;
      }
      if (!result.ok || !result.data) {
        setAccess('error');
        return;
      }

      setAccess('authorized');
      selectedUsernameRef.current = username;
      setSelectedUsername(username);
      applyEmploymentForm(result.data);
      await loadAccounts(username);
    };

    initialize();
    return () => {
      mounted.current = false;
      loadSequence.current += 1;
    };
  }, [applyEmploymentForm, loadAccounts, privilegedByRole, username]);

  const latestPlannedType =
    employmentForm?.history[0]?.newContractType ?? employmentForm?.contractType;

  const validate = () => {
    if (!contractType || !effectiveDate || !reason.trim()) {
      return 'EMPLOYMENT_FORM.VALIDATION.REQUIRED';
    }
    if (reason.trim().length > 2000) {
      return 'EMPLOYMENT_FORM.VALIDATION.REASON_LENGTH';
    }
    if (contractType === latestPlannedType) {
      return 'EMPLOYMENT_FORM.VALIDATION.SAME_TYPE';
    }
    return '';
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const error = validate();
    setValidationError(error);
    setUpdateError('');
    setUpdateSuccess(false);
    if (error || !selectedUsername) {
      return;
    }

    const targetUsername = selectedUsername;
    setUpdating(true);
    const result = await updateEmploymentForm(targetUsername, {
      contractType,
      effectiveDate,
      reason: reason.trim(),
    });
    if (!mounted.current) {
      return;
    }
    setUpdating(false);
    if (targetUsername !== selectedUsernameRef.current) {
      setStaleResponse(true);
      return;
    }
    if (result.status === 403) {
      setAccess('unauthorized');
      return;
    }
    if (!result.ok || !result.data) {
      setUpdateError(
        result.status === 400
          ? 'EMPLOYMENT_FORM.VALIDATION.API'
          : 'EMPLOYMENT_FORM.UPDATE_ERROR',
      );
      return;
    }
    applyEmploymentForm(result.data);
    setUpdateSuccess(true);
  };

  if (access === 'checking') {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress
          aria-label={intl.formatMessage({ id: 'EMPLOYMENT_FORM.LOADING' })}
        />
      </Box>
    );
  }

  if (access === 'unauthorized') {
    return (
      <Box p={4}>
        <Alert severity="warning">
          {intl.formatMessage({ id: 'EMPLOYMENT_FORM.UNAUTHORIZED' })}
        </Alert>
      </Box>
    );
  }

  if (access === 'error') {
    return (
      <Box p={4}>
        <Alert severity="error">
          {intl.formatMessage({ id: 'EMPLOYMENT_FORM.ACCESS_ERROR' })}
        </Alert>
      </Box>
    );
  }

  const today = moment().format('YYYY-MM-DD');
  const futureDated = effectiveDate > today;

  return (
    <Box p={3}>
      <PageHeader
        title={intl.formatMessage({ id: 'EMPLOYMENT_FORM.TITLE' })}
        description={intl.formatMessage({ id: 'EMPLOYMENT_FORM.DESCRIPTION' })}
      />

      {accountsError && (
        <Alert severity="error">
          {intl.formatMessage({ id: 'EMPLOYMENT_FORM.ACCOUNTS_ERROR' })}
        </Alert>
      )}
      {!accountsError && accounts.length === 0 && !employmentForm && (
        <Alert severity="info">
          {intl.formatMessage({ id: 'EMPLOYMENT_FORM.ACCOUNTS_EMPTY' })}
        </Alert>
      )}

      {accounts.length > 0 && (
        <Box maxWidth={520} mb={3}>
          <Autocomplete
            id="employment-account"
            options={accounts}
            value={
              accounts.find(({ username }) => username === selectedUsername) ||
              null
            }
            getOptionLabel={accountLabel}
            getOptionSelected={(option, value) =>
              option.username === value.username
            }
            onChange={(_event, account) =>
              account && loadForm(account.username)
            }
            disabled={updating}
            disableClearable={Boolean(selectedUsername)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={intl.formatMessage({ id: 'EMPLOYMENT_FORM.ACCOUNT' })}
              />
            )}
          />
        </Box>
      )}

      {staleResponse && (
        <Box mb={2}>
          <Alert severity="info">
            {intl.formatMessage({ id: 'EMPLOYMENT_FORM.STALE_RESPONSE' })}
          </Alert>
        </Box>
      )}
      {formError && (
        <Alert severity="error">
          {intl.formatMessage({ id: 'EMPLOYMENT_FORM.LOAD_ERROR' })}
        </Alert>
      )}
      {loadingForm && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {!loadingForm && employmentForm && (
        <>
          <Paper elevation={2} style={{ padding: '1.5rem' }}>
            <Typography variant="h6">
              {`${employmentForm.name} ${employmentForm.surname}`}
            </Typography>
            <Typography paragraph color="textSecondary">
              {employmentForm.username}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              {intl.formatMessage({ id: 'EMPLOYMENT_FORM.CURRENT' })}:{' '}
              {intl.formatMessage({
                id: contractLabel(employmentForm.contractType),
              })}
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    id="employment-contract-type"
                    select
                    SelectProps={{ native: true }}
                    fullWidth
                    variant="outlined"
                    label={intl.formatMessage({
                      id: 'EMPLOYMENT_FORM.NEW_TYPE',
                    })}
                    value={contractType}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setContractType(event.target.value as ContractType)
                    }
                  >
                    {CONTRACT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {intl.formatMessage({ id: contractLabel(type) })}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    id="employment-effective-date"
                    fullWidth
                    variant="outlined"
                    type="date"
                    label={intl.formatMessage({
                      id: 'EMPLOYMENT_FORM.EFFECTIVE_DATE',
                    })}
                    InputLabelProps={{ shrink: true }}
                    value={effectiveDate}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setEffectiveDate(event.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info">
                    {intl.formatMessage({
                      id: futureDated
                        ? 'EMPLOYMENT_FORM.FUTURE_NOTE'
                        : 'EMPLOYMENT_FORM.IMMEDIATE_NOTE',
                    })}
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="warning">
                    {intl.formatMessage({ id: 'EMPLOYMENT_FORM.ORDERS_NOTE' })}
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="employment-reason"
                    fullWidth
                    multiline
                    minRows={3}
                    variant="outlined"
                    label={intl.formatMessage({ id: 'EMPLOYMENT_FORM.REASON' })}
                    value={reason}
                    inputProps={{ maxLength: 2000 }}
                    helperText={`${reason.length}/2000`}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setReason(event.target.value)
                    }
                  />
                </Grid>
                {validationError && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      {intl.formatMessage({ id: validationError })}
                    </Alert>
                  </Grid>
                )}
                {updateError && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      {intl.formatMessage({ id: updateError })}
                    </Alert>
                  </Grid>
                )}
                {updateSuccess && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      {intl.formatMessage({ id: 'EMPLOYMENT_FORM.UPDATED' })}
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12} container justifyContent="flex-end">
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    disabled={updating}
                  >
                    {intl.formatMessage({
                      id: updating
                        ? 'EMPLOYMENT_FORM.SAVING'
                        : 'EMPLOYMENT_FORM.SAVE',
                    })}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>

          <Box mt={4}>
            <Typography variant="h5" gutterBottom>
              {intl.formatMessage({ id: 'EMPLOYMENT_FORM.HISTORY' })}
            </Typography>
            {employmentForm.history.length === 0 ? (
              <Alert severity="info">
                {intl.formatMessage({ id: 'EMPLOYMENT_FORM.HISTORY_EMPTY' })}
              </Alert>
            ) : (
              <Paper elevation={2} style={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {intl.formatMessage({
                          id: 'EMPLOYMENT_FORM.HISTORY.CHANGE',
                        })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({
                          id: 'EMPLOYMENT_FORM.EFFECTIVE_DATE',
                        })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({
                          id: 'EMPLOYMENT_FORM.HISTORY.STATUS',
                        })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({
                          id: 'EMPLOYMENT_FORM.HISTORY.ACTOR',
                        })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({ id: 'EMPLOYMENT_FORM.REASON' })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({
                          id: 'EMPLOYMENT_FORM.HISTORY.CHANGED_AT',
                        })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({
                          id: 'EMPLOYMENT_FORM.HISTORY.APPLIED_AT',
                        })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employmentForm.history.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          {`${intl.formatMessage({
                            id: contractLabel(entry.oldContractType),
                          })} → ${intl.formatMessage({
                            id: contractLabel(entry.newContractType),
                          })}`}
                        </TableCell>
                        <TableCell>{entry.effectiveDate}</TableCell>
                        <TableCell>
                          {intl.formatMessage({
                            id: `EMPLOYMENT_FORM.STATUS.${historyStatus(
                              entry,
                              today,
                            )}`,
                          })}
                        </TableCell>
                        <TableCell>
                          {`${entry.actorName} (${entry.actorUsername}, #${entry.actorId})`}
                        </TableCell>
                        <TableCell>{entry.reason}</TableCell>
                        <TableCell>
                          {formatTimestamp(entry.changedAt)}
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(entry.appliedAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default EmploymentForms;
